import React, { useState } from 'react'
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import { colors } from '~constants/colorsPallete'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import TelegramIcon from '@mui/icons-material/Telegram'
import DownloadIcon from '@mui/icons-material/Download'
import { operatorStatusLabels } from '~constants/operatorStatus'
import { toast } from 'react-toastify'

interface ReceiptsFiltersProps {
  onFilterChange: (filters: InvoiceFilters) => void
  selectedInvoiceIds?: string[]
  onCobrarClick?: () => void
  onDownloadClick?: () => void
  businessNickname?: string[]
}

export type InvoiceFilters = {
  start_competence_date: string
  end_competence_date: string
  invoice_status: string
  companyName: string
  operator_status: string
  business_nickname: string
  business_name: string
  order_by?: string
  order_direction?: 'asc' | 'desc'
}

const ReceiptsFilters: React.FC<ReceiptsFiltersProps> = ({
  onFilterChange,
  selectedInvoiceIds = [],
  onCobrarClick,
  onDownloadClick,
}) => {
  const [start_competence_date, setStartCompetenceDate] = useState<Dayjs | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [end_competence_date, setEndCompetenceDate] = useState<Dayjs | null>(null)
  const [invoice_status, setInvoiceStatus] = React.useState<string>('')
  const [operator_status, setOperatorStatus] = React.useState<string>('')
  const [selectedCompanyName, setSelectedCompanyName] = React.useState<string | string[]>('')
  const [business_nickname, setBusinessNickname] = useState<string>('')
  const [business_name, setBusinessName] = useState<string>('')

  const hasSelectedInvoices = selectedInvoiceIds.length > 0

  const initialFilters: InvoiceFilters = {
    start_competence_date: '',
    end_competence_date: '',
    invoice_status: '',
    companyName: '',
    operator_status: '',
    business_nickname: '',
    business_name: '',
    order_by: 'competence_date',
    order_direction: 'desc',
  }

  const clearFilters = () => {
    // Reset all filter states
    setOperatorStatus('')
    setInvoiceStatus('')
    setStartCompetenceDate(null)
    setEndCompetenceDate(null)
    setSelectedCompanyName('')
    setBusinessNickname('')
    setBusinessName('')

    onFilterChange(initialFilters)
  }

  const validateDatesAndFilter = () => {
    // Check if start date is after end date (only if both are provided)
    if (start_competence_date && end_competence_date && start_competence_date.isAfter(end_competence_date)) {
      toast.error('A data de início deve ser anterior à data de fim.')
      return
    }

    onFilterChange({
      start_competence_date: start_competence_date ? start_competence_date.format('YYYY/MM/DD') : '',
      invoice_status,
      end_competence_date: end_competence_date ? end_competence_date.format('YYYY/MM/DD') : '',
      companyName: selectedCompanyName,
      operator_status,
      business_nickname,
      business_name,
    } as InvoiceFilters)
  }

  const onFilter = () => {
    validateDatesAndFilter()
  }

  // Render action buttons (Baixar, Cobrar, and Filter toggle)
  const renderActionButtons = () => (
    <Grid container direction="row" justifyContent="flex-end" mb={2} spacing={2}>
      <Grid item>
        <Button
          disableElevation
          variant="contained"
          startIcon={<DownloadIcon />}
          sx={{
            backgroundColor: colors['blue.light'],
            borderRadius: 100,
            '&:hover': {
              backgroundColor: colors.blue,
            },
            '&.Mui-disabled': {
              backgroundColor: colors['gray.300'],
              color: colors['gray.500'],
            },
          }}
          disabled={!hasSelectedInvoices}
          onClick={onDownloadClick}
        >
          Baixar ({selectedInvoiceIds.length})
        </Button>
      </Grid>
      <Grid item>
        <Button
          disableElevation
          variant="contained"
          startIcon={<TelegramIcon />}
          sx={{
            backgroundColor: colors['green.default'],
            borderRadius: 100,
            '&:hover': {
              backgroundColor: colors['green.600'],
            },
            '&.Mui-disabled': {
              backgroundColor: colors['gray.300'],
              color: colors['gray.500'],
            },
          }}
          disabled={!hasSelectedInvoices}
          onClick={onCobrarClick}
        >
          Cobrar ({selectedInvoiceIds.length})
        </Button>
      </Grid>
      <Grid item>
        <Button
          disableElevation
          variant="contained"
          endIcon={<FilterAltRoundedIcon />}
          sx={{ backgroundColor: colors['gray.500'], borderRadius: 100 }}
          onClick={() => {
            setIsOpen(!isOpen)
          }}
        >
          {isOpen ? 'Fechar Filtros' : 'Abrir Filtros'}
        </Button>
      </Grid>
    </Grid>
  )

  return (
    <>
      {renderActionButtons()}

      {isOpen && (
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          p={3}
          mb={2}
          border={`1px solid ${colors['gray.100']}`}
          borderRadius={'3px'}
          sx={{ backgroundColor: colors['white.300'] }}
        >
          <Grid item md={4} xs={12}>
            <FormControl fullWidth>
              <TextField
                label="Razão social"
                placeholder="Digite para pesquisar pela razão social"
                value={business_name}
                onChange={(e) => setBusinessName(e.target.value)}
                sx={{
                  backgroundColor: colors.white,
                  borderRadius: 100,
                  border: 0,
                  '& fieldset': { border: 'none' },
                }}
              />
            </FormControl>
          </Grid>

          <Grid item md={2} xs={12}>
            <FormControl fullWidth>
              <TextField
                label="Unidade (apelido)"
                placeholder="Digite para pesquisar qualquer unidade"
                value={business_nickname}
                onChange={(e) => setBusinessNickname(e.target.value)}
                sx={{
                  backgroundColor: colors.white,
                  borderRadius: 100,
                  border: 0,
                  '& fieldset': { border: 'none' },
                }}
              />
            </FormControl>
          </Grid>

          <Grid item md={2} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Status do operador</InputLabel>
              <Select
                value={operator_status}
                sx={{
                  backgroundColor: colors.white,
                  borderRadius: 100,
                  border: 0,
                  '& fieldset': { border: 'none' },
                }}
                onChange={(e) => setOperatorStatus(e.target.value)}
              >
                <MenuItem value={''}>Todas</MenuItem>
                {Object.entries(operatorStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={2} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Status</InputLabel>
              <Select
                id="demo-customized-select-native"
                value={invoice_status}
                sx={{
                  backgroundColor: colors.white,
                  borderRadius: 100,
                  border: 0,
                  '& fieldset': { border: 'none' },
                }}
                onChange={(e) => setInvoiceStatus(e.target.value)}
              >
                <MenuItem value={''}>Todas</MenuItem>
                <MenuItem value={'null'}>Não recebida</MenuItem>
                <MenuItem value={'pending'}>Processando</MenuItem>
                <MenuItem value={'validated'}>Validada</MenuItem>
                <MenuItem value={'refused'}>Recusada</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              <FormControl fullWidth>
                <DatePicker
                  label="Início da data de competência"
                  format="DD/MM/YYYY"
                  views={['year', 'month', 'day']}
                  value={start_competence_date}
                  sx={{
                    backgroundColor: colors.white,
                    borderRadius: 100,
                    border: 0,
                    '& fieldset': { border: 'none' },
                  }}
                  onChange={(newValue) => {
                    setStartCompetenceDate(newValue)
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </Grid>
          <Grid item md={3} xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              <FormControl fullWidth>
                <DatePicker
                  label="Fim da data de competência"
                  format="DD/MM/YYYY"
                  views={['year', 'month', 'day']}
                  minDate={start_competence_date || undefined}
                  value={end_competence_date}
                  sx={{
                    backgroundColor: colors.white,
                    borderRadius: 100,
                    border: 0,
                    '& fieldset': { border: 'none' },
                  }}
                  onChange={(newValue) => {
                    setEndCompetenceDate(newValue)
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </Grid>

          <Grid item md={12} xs={12}>
            <Box display="flex" flexDirection="row" alignContent={'center'} justifyContent={'flex-end'} mt={3}>
              <Button
                disableElevation
                variant="contained"
                sx={{ backgroundColor: colors.success, borderRadius: 100, mr: 2 }}
                onClick={onFilter}
              >
                Filtrar
              </Button>
              <Button
                disableElevation
                variant="outlined"
                sx={{ borderColor: colors.error, color: colors.error, borderRadius: 100 }}
                onClick={clearFilters}
              >
                Limpar filtros
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default ReceiptsFilters
